package com.bulut.mecitogullari.ui.screens.supplierdetail

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Share
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.core.export.DebtStatementHtml
import com.bulut.mecitogullari.core.platform.isPrintSupported
import com.bulut.mecitogullari.core.platform.printHtml
import com.bulut.mecitogullari.core.util.formatAmountPrecise
import com.bulut.mecitogullari.core.util.formatDateShort
import com.bulut.mecitogullari.core.util.parseDecimalAmount
import com.bulut.mecitogullari.core.util.todayUtcMidnightMillis
import com.bulut.mecitogullari.data.model.AccountKind
import com.bulut.mecitogullari.data.model.DebtEntry
import com.bulut.mecitogullari.data.model.DebtEntryType
import com.bulut.mecitogullari.ui.components.DateField
import com.bulut.mecitogullari.ui.components.DecimalAmountField
import com.bulut.mecitogullari.ui.components.EmptyState
import com.bulut.mecitogullari.ui.theme.AppColors
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import kotlin.math.abs
import kotlin.math.roundToLong

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SupplierDetailScreen(
    supplierId: String,
    onAddPurchase: () -> Unit,
    onEditPurchase: (entryId: String) -> Unit,
    onBack: () -> Unit,
    viewModel: SupplierDetailViewModel = koinViewModel { parametersOf(supplierId) },
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val supplierDeleted by viewModel.supplierDeleted.collectAsStateWithLifecycle()
    var showEntryDialog by remember { mutableStateOf(false) }
    var showDeleteConfirm by remember { mutableStateOf(false) }
    var entryToDelete by remember { mutableStateOf<DebtEntry?>(null) }

    LaunchedEffect(supplierDeleted) {
        if (supplierDeleted) onBack()
    }

    val isCustomer = uiState.supplier?.kind == AccountKind.CUSTOMER
    val strings = LocalStrings.current

    Scaffold(
        containerColor = AppColors.Background,
        topBar = {
            TopAppBar(
                title = { Text(uiState.supplier?.name ?: strings.pdfStatement) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = strings.back)
                    }
                },
                actions = {
                    if (isPrintSupported) {
                        IconButton(
                            onClick = {
                                uiState.supplier?.let { supplier ->
                                    val safeName = supplier.name.lowercase()
                                        .map { if (it.isLetterOrDigit()) it else '-' }
                                        .joinToString("")
                                        .trim('-')
                                    printHtml(
                                        html = DebtStatementHtml.build(
                                            supplier = supplier,
                                            entries = uiState.months.flatMap { it.entries },
                                        ),
                                        fileName = "ekstre-$safeName.pdf",
                                    )
                                }
                            },
                        ) {
                            Icon(Icons.Default.Share, contentDescription = strings.printPdf)
                        }
                    }
                    IconButton(onClick = { showDeleteConfirm = true }) {
                        Icon(Icons.Default.Delete, contentDescription = strings.deleteAccountAction, tint = AppColors.Expense)
                    }
                },
            )
        },
    ) { padding ->
        LazyColumn(
            modifier = Modifier.fillMaxSize().padding(padding),
            contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 8.dp, bottom = 32.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            item {
                BalanceCard(
                    balance = uiState.balance,
                    totalPurchases = uiState.totalPurchases,
                    totalPayments = uiState.totalPayments,
                    phone = uiState.supplier?.phone.orEmpty(),
                    isCustomer = isCustomer,
                )
            }

            item {
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    Button(
                        onClick = onAddPurchase,
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                        contentPadding = PaddingValues(horizontal = 8.dp),
                        modifier = Modifier.weight(1f).height(46.dp),
                    ) {
                        Text(if (isCustomer) strings.materialSale else strings.materialPurchase, maxLines = 1, overflow = TextOverflow.Ellipsis)
                    }
                    OutlinedButton(
                        onClick = { showEntryDialog = true },
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.outlinedButtonColors(contentColor = AppColors.Income),
                        contentPadding = PaddingValues(horizontal = 8.dp),
                        modifier = Modifier.weight(1f).height(46.dp),
                    ) {
                        Text(if (isCustomer) strings.collectionOrCarry else strings.paymentOrCarry, maxLines = 1, overflow = TextOverflow.Ellipsis)
                    }
                }
            }

            if (uiState.months.isEmpty()) {
                item {
                    EmptyState(
                        if (isCustomer) strings.noEntriesCustomer else strings.noEntriesSupplier
                    )
                }
            }

            uiState.months.forEach { group ->
                item(key = "month-${group.month.year}-${group.month.month}") {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = group.month.label,
                            style = MaterialTheme.typography.titleSmall,
                        )
                        Text(
                            text = buildString {
                                if (group.purchases > 0) {
                                    append(if (isCustomer) strings.salePrefix + " " else strings.purchasePrefix + " ")
                                    append(formatAmountPrecise(group.purchases))
                                }
                                if (group.payments > 0) {
                                    if (isNotEmpty()) append("  •  ")
                                    append(if (isCustomer) strings.pdfCollection + " " else strings.pdfPayment + " ")
                                    append(formatAmountPrecise(group.payments))
                                }
                            },
                            style = MaterialTheme.typography.labelMedium,
                            color = AppColors.TextSecondary,
                        )
                    }
                }
                items(group.entries, key = { it.id }) { entry ->
                    DebtEntryCard(
                        entry = entry,
                        isCustomer = isCustomer,
                        onClick = {
                            if (entry.type == DebtEntryType.PURCHASE && entry.items.isNotEmpty()) {
                                onEditPurchase(entry.id)
                            } else {
                                entryToDelete = entry
                            }
                        },
                    )
                }
            }
        }
    }

    if (showEntryDialog) {
        ManualEntryDialog(
            isCustomer = isCustomer,
            onDismiss = { showEntryDialog = false },
            onSave = { type, amount, date, description ->
                viewModel.addManualEntry(type, amount, date, description)
                showEntryDialog = false
            },
        )
    }

    entryToDelete?.let { entry ->
        AlertDialog(
            onDismissRequest = { entryToDelete = null },
            title = { Text(strings.deleteRecordTitle) },
            text = {
                Text(
                    strings.deleteRecordMessage.fillTemplate(
                        "desc" to entry.description.ifBlank { strings.thisRecord },
                        "amount" to formatAmountPrecise(entry.amount),
                    )
                )
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        viewModel.deleteEntry(entry.id)
                        entryToDelete = null
                    },
                ) {
                    Text(strings.delete, color = AppColors.Expense)
                }
            },
            dismissButton = {
                TextButton(onClick = { entryToDelete = null }) { Text(strings.cancel) }
            },
        )
    }

    if (showDeleteConfirm) {
        AlertDialog(
            onDismissRequest = { showDeleteConfirm = false },
            title = { Text(if (isCustomer) strings.deleteCustomerTitle else strings.deleteSupplierTitle) },
            text = {
                Text(
                    strings.deleteAccountMessage
                        .fillTemplate("name" to uiState.supplier?.name.orEmpty())
                )
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        showDeleteConfirm = false
                        viewModel.deleteSupplier()
                    },
                ) {
                    Text(strings.delete, color = AppColors.Expense)
                }
            },
            dismissButton = {
                TextButton(onClick = { showDeleteConfirm = false }) { Text(strings.cancel) }
            },
        )
    }
}

@Composable
private fun BalanceCard(
    balance: Double,
    totalPurchases: Double,
    totalPayments: Double,
    phone: String,
    isCustomer: Boolean,
) {
    val strings = LocalStrings.current
    // Positive balance: supplier -> our debt (red); customer -> our receivable (green).
    val positiveIsReceivable = isCustomer
    Card(
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = AppColors.NavyDeep),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            Text(
                text = if ((balance >= 0) == positiveIsReceivable) strings.pdfCurrentReceivable else strings.pdfCurrentDebt,
                style = MaterialTheme.typography.labelLarge,
                color = Color.White.copy(alpha = 0.8f),
            )
            Text(
                text = formatAmountPrecise(abs(balance)),
                style = MaterialTheme.typography.headlineMedium,
                color = if (balance == 0.0) Color.White
                else if ((balance > 0) == positiveIsReceivable) Color(0xFF4ADE80) else Color(0xFFF87171),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            HorizontalDivider(
                modifier = Modifier.padding(vertical = 12.dp),
                color = Color.White.copy(alpha = 0.15f),
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Column {
                    Text(
                        if (isCustomer) strings.pdfTotalSales else strings.pdfTotalPurchases,
                        style = MaterialTheme.typography.labelSmall,
                        color = Color.White.copy(alpha = 0.7f),
                    )
                    Text(
                        formatAmountPrecise(totalPurchases),
                        style = MaterialTheme.typography.bodyMedium,
                        color = Color.White,
                    )
                }
                Column(horizontalAlignment = Alignment.End) {
                    Text(
                        if (isCustomer) strings.pdfTotalCollections else strings.pdfTotalPayments,
                        style = MaterialTheme.typography.labelSmall,
                        color = Color.White.copy(alpha = 0.7f),
                    )
                    Text(
                        formatAmountPrecise(totalPayments),
                        style = MaterialTheme.typography.bodyMedium,
                        color = Color(0xFF4ADE80),
                    )
                }
            }
            if (phone.isNotBlank()) {
                Text(
                    text = phone,
                    style = MaterialTheme.typography.bodySmall,
                    color = Color.White.copy(alpha = 0.6f),
                    modifier = Modifier.padding(top = 10.dp),
                )
            }
        }
    }
}

@Composable
private fun DebtEntryCard(
    entry: DebtEntry,
    isCustomer: Boolean,
    onClick: () -> Unit,
) {
    val strings = LocalStrings.current
    val isPurchase = entry.type == DebtEntryType.PURCHASE
    // Tedarikçi: alım kırmızı, ödeme yeşil. Müşteri: satış yeşil, tahsilat lacivert.
    val amountColor = when {
        isPurchase && isCustomer -> AppColors.Income
        isPurchase -> AppColors.Expense
        isCustomer -> AppColors.NavyDeep
        else -> AppColors.Income
    }
    Card(
        shape = RoundedCornerShape(14.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
    ) {
        Column(modifier = Modifier.padding(14.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = entry.description.ifBlank {
                            when {
                                isPurchase && isCustomer -> strings.materialSaleEntry
                                isPurchase -> strings.materialPurchaseEntry
                                isCustomer -> "Tahsilat"
                                else -> strings.pdfPayment
                            }
                        },
                        style = MaterialTheme.typography.bodyLarge,
                        fontWeight = FontWeight.Medium,
                    )
                    Text(
                        text = formatDateShort(entry.dateEpochMillis) +
                            if (entry.items.isNotEmpty()) "  •  ${entry.items.size} kalem" else "",
                        style = MaterialTheme.typography.bodySmall,
                        color = AppColors.TextSecondary,
                    )
                }
                Text(
                    text = (if (isPurchase) "+" else "−") + formatAmountPrecise(entry.amount),
                    style = MaterialTheme.typography.titleSmall,
                    color = amountColor,
                )
            }
            if (entry.items.isNotEmpty()) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 10.dp)
                        .background(AppColors.SurfaceMuted, RoundedCornerShape(10.dp))
                        .padding(10.dp),
                    verticalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    entry.items.forEach { item ->
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                        ) {
                            Text(
                                text = "${item.name} — ${formatQuantity(item.quantity)} ${item.unit} × ${formatAmountPrecise(item.unitPrice)}",
                                style = MaterialTheme.typography.bodySmall,
                                color = AppColors.TextSecondary,
                                modifier = Modifier.weight(1f),
                            )
                            Text(
                                text = formatAmountPrecise(item.total),
                                style = MaterialTheme.typography.bodySmall,
                                color = AppColors.TextPrimary,
                            )
                        }
                    }
                }
            }
        }
    }
}

private fun formatQuantity(quantity: Double): String =
    if (quantity == quantity.roundToLong().toDouble()) {
        quantity.roundToLong().toString()
    } else {
        quantity.toString().replace('.', ',')
    }

/**
 * Ödeme veya devreden borç girişi — Yeni İşlem ekranıyla aynı dil:
 * renkli segment seçici, tarih, tutar, açıklama.
 */
@Composable
private fun ManualEntryDialog(
    isCustomer: Boolean,
    onDismiss: () -> Unit,
    onSave: (type: DebtEntryType, amount: Double, dateEpochMillis: Long, description: String) -> Unit,
) {
    val strings = LocalStrings.current
    var type by remember { mutableStateOf(DebtEntryType.PAYMENT) }
    var amountText by remember { mutableStateOf("") }
    var date by remember { mutableStateOf(todayUtcMidnightMillis()) }
    var description by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(
                modifier = Modifier.padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                Text(
                    text = when {
                        type == DebtEntryType.PAYMENT && isCustomer -> "Tahsilat Al"
                        type == DebtEntryType.PAYMENT -> strings.makePayment
                        isCustomer -> strings.addCarryReceivable
                        else -> "Devir Borcu Ekle"
                    },
                    style = MaterialTheme.typography.titleMedium,
                )

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(AppColors.SurfaceMuted, RoundedCornerShape(12.dp))
                        .padding(4.dp),
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    TypeOption(if (isCustomer) strings.pdfCollection else strings.pdfPayment, type == DebtEntryType.PAYMENT, AppColors.Income, Modifier.weight(1f)) {
                        type = DebtEntryType.PAYMENT
                        error = null
                    }
                    TypeOption(if (isCustomer) strings.carryReceivable else strings.carryDebt, type == DebtEntryType.PURCHASE, AppColors.Expense, Modifier.weight(1f)) {
                        type = DebtEntryType.PURCHASE
                        error = null
                    }
                }

                DateField(
                    label = strings.dateLabel,
                    dateEpochMillis = date,
                    onDateSelected = { date = it },
                    modifier = Modifier.fillMaxWidth(),
                )

                DecimalAmountField(
                    label = strings.amountFieldLabel,
                    value = amountText,
                    onValueChange = {
                        amountText = it
                        error = null
                    },
                    modifier = Modifier.fillMaxWidth(),
                )

                OutlinedTextField(
                    value = description,
                    onValueChange = { description = it },
                    label = {
                        Text(
                            if (type == DebtEntryType.PAYMENT) strings.descPaymentHint
                            else strings.descCarryHint
                        )
                    },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth(),
                )

                if (type == DebtEntryType.PURCHASE) {
                    Text(
                        text = if (isCustomer) strings.hintCarryReceivable else strings.hintCarryDebt,
                        style = MaterialTheme.typography.bodySmall,
                        color = AppColors.TextSecondary,
                    )
                }

                error?.let {
                    Text(it, style = MaterialTheme.typography.bodySmall, color = AppColors.Expense)
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    TextButton(onClick = onDismiss, modifier = Modifier.weight(1f)) {
                        Text(strings.cancel, color = AppColors.TextSecondary)
                    }
                    Button(
                        onClick = {
                            val amount = parseDecimalAmount(amountText)
                            if (amount == null || amount <= 0.0) {
                                error = strings.enterValidAmount
                                return@Button
                            }
                            onSave(type, amount, date, description)
                        },
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                        modifier = Modifier.weight(1f).height(44.dp),
                    ) {
                        Text(strings.save, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
    }
}

@Composable
private fun TypeOption(
    text: String,
    isSelected: Boolean,
    activeColor: Color,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    Row(
        modifier = modifier
            .background(
                color = if (isSelected) activeColor else Color.Transparent,
                shape = RoundedCornerShape(9.dp),
            )
            .clickable(onClick = onClick)
            .padding(vertical = 10.dp),
        horizontalArrangement = Arrangement.Center,
    ) {
        Text(
            text = text,
            color = if (isSelected) Color.White else AppColors.TextSecondary,
            style = MaterialTheme.typography.labelLarge,
            maxLines = 1,
        )
    }
}
